/**
 * Sjednocené design tokeny pro bottom sheety / modaly v cockpit Moje směna.
 *
 * Glassmorphism: vícevrstvý povrch (semi-transparent base + jemný gradient +
 * inner top-edge highlight) s `backdrop-filter: blur + saturate` pro vizuální
 * dojem skutečného skla. Inspirace Apple iOS modal sheets — barva pozadí
 * prosvítá, světlo se odráží od horní hrany, dolní hrana má lehký stín
 * "tloušťky skla".
 *
 *   getGlassSurface(isDark) → { background, backdropFilter, border, boxShadow, color }
 *
 * Použití:
 *   <div style={getGlassSurface(isDark)}>...</div>
 *
 * Pro form komponenty (input/button/section) jsou helpers v SheetUI.jsx;
 * tady jen tokeny + helper pro povrch sheetu samotného.
 */

export const SHEET_TOKENS = {
    radius: {
        container: 20,       // sheet roh nahoru (mobile)
        containerDesktop: 18,
        card:      14,       // vnitřní karty (slot, sekce, alert)
        input:     10,       // input/textarea
        button:    12,       // button — touch-friendly, ne moc oblý
        chip:      999,      // pill / badge
    },
    spacing: {
        sheetPadX:    16,    // horizontální padding obsahu sheetu
        sheetPadY:    16,
        headerPadY:   14,
        sectionGap:   18,    // mezi sekcemi
        rowGap:       12,
        controlGap:   8,     // mezi labelem a controlem (vertikálně)
    },
    typography: {
        title:    { size: 17, weight: 700, letterSpacing: -0.2 },  // velký titulek (PhaseModal)
        actionLabel: { size: 11, weight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }, // dot + label (ClockInManual)
        section:  { size: 12, weight: 700, letterSpacing: 0.3, textTransform: 'uppercase' },
        body:     { size: 14, weight: 500 },
        bodyBold: { size: 14, weight: 700 },
        label:    { size: 11, weight: 600, letterSpacing: 0.3, textTransform: 'uppercase' },
        hint:     { size: 11.5, weight: 500 },
        mono:     { size: 14, weight: 700, fontVariantNumeric: 'tabular-nums' },
    },
    sizes: {
        inputHeight:  44,    // touch target
        buttonHeight: 48,    // primární CTA — větší pro klepání palcem
        iconBox:      34,    // ikona v dlaždici / inputu
        dragHandle:   { width: 38, height: 4 },
        closeBtn:     36,
    },
    backdrop: {
        background:    'rgba(0,0,0,0.55)',   // o trochu měně neprůhledné než dřív (0.7) — vidět víc okolí přes blur
        backdropFilter:'blur(10px)',          // víc rozostření pozadí
    },
    handle: {
        light: 'rgba(0,0,0,0.20)',
        dark:  'rgba(255,255,255,0.22)',
    },
};

/**
 * Vícevrstvý glass povrch pro sheet container.
 * Layer 1 (background): subtle gradient + semi-transparent base color
 * Layer 2 (backdrop-filter): blur + saturate boost (Apple-like vivid colors)
 * Layer 3 (border): tenký okraj pro definovanou hranu
 * Layer 4 (box-shadow): vnější soft drop + inset top highlight (edge of glass)
 */
export function getGlassSurface(isDark) {
    if (isDark) {
        return {
            // Nižší krytnost než dřív (0,62 → 0,44): podklad má **prosvítat**.
            // Skleněná deska, přes kterou není nic vidět, je jen tmavý panel.
            background: 'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.02) 42%, rgba(0,0,0,0.10) 100%), rgba(24,22,32,0.44)',
            // `brightness` navíc: sklo má vypadat prosvětleně, ne zakouřeně.
            backdropFilter: 'blur(32px) saturate(200%) brightness(1.08)',
            WebkitBackdropFilter: 'blur(32px) saturate(200%) brightness(1.08)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: [
                '0 1px 0 rgba(255,255,255,0.38) inset',    // horní hrana — odlesk, kde se sklo láme
                '1px 0 0 rgba(255,255,255,0.10) inset',    // levá hrana, slabší
                '-1px 0 0 rgba(255,255,255,0.08) inset',   // pravá hrana, ještě slabší
                '0 -1px 0 rgba(0,0,0,0.28) inset',         // spodní hrana — tmavá, dělá tloušťku
                '0 18px 50px -12px rgba(0,0,0,0.62), 0 6px 20px -6px rgba(0,0,0,0.42)',
            ].join(', '),
            color: 'rgba(255,255,255,0.92)',
        };
    }
    return {
        background: 'linear-gradient(180deg, rgba(255,255,255,0.58) 0%, rgba(255,255,255,0.26) 100%), rgba(255,255,255,0.48)',
        backdropFilter: 'blur(32px) saturate(200%) brightness(1.04)',
        WebkitBackdropFilter: 'blur(32px) saturate(200%) brightness(1.04)',
        border: '1px solid rgba(255,255,255,0.62)',
        boxShadow: [
            '0 1px 0 rgba(255,255,255,0.90) inset',
            '1px 0 0 rgba(255,255,255,0.55) inset',
            '-1px 0 0 rgba(255,255,255,0.45) inset',
            '0 -1px 0 rgba(0,0,0,0.07) inset',
            '0 18px 50px -12px rgba(0,0,0,0.20), 0 6px 20px -6px rgba(0,0,0,0.10)',
        ].join(', '),
        color: 'rgba(0,0,0,0.92)',
    };
}

/** Subtle "card inside sheet" — výrazně tlumený, jen okraj a back rgba */
export function getInnerCardSurface(isDark) {
    if (isDark) {
        return {
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
        };
    }
    return {
        background: 'rgba(0,0,0,0.025)',
        border: '1px solid rgba(0,0,0,0.06)',
    };
}

/** Textové barvy podle tmavého/světlého režimu — vždy stejné napříč sheety. */
export function getTextColors(isDark) {
    if (isDark) return {
        textPrimary:   '#fff',
        textSecondary: 'rgba(255,255,255,0.72)',
        textMuted:     'rgba(255,255,255,0.50)',
        labelMuted:    'rgba(255,255,255,0.55)',
        divider:       'rgba(255,255,255,0.10)',
        accent:        '#FF8A00',
        success:       '#10B981',
        danger:        '#EF4444',
    };
    return {
        textPrimary:   'var(--text-primary, #111)',
        textSecondary: 'var(--text-secondary, #333)',
        textMuted:     'var(--text-muted, #666)',
        labelMuted:    'var(--text-secondary, #555)',
        divider:       'rgba(0,0,0,0.08)',
        accent:        '#FF8A00',
        success:       '#10B981',
        danger:        '#EF4444',
    };
}

/** Styl pro backdrop layer (fixed inset 0 přes celou obrazovku). */
export function getBackdropStyle() {
    return {
        position: 'fixed', inset: 0, zIndex: 1000,
        background: SHEET_TOKENS.backdrop.background,
        backdropFilter: SHEET_TOKENS.backdrop.backdropFilter,
        WebkitBackdropFilter: SHEET_TOKENS.backdrop.backdropFilter,
        display: 'flex', flexDirection: 'column',
    };
}

/** Drag handle (mobile) — pruh v horní části sheetu, signalizuje swipe-down close. */
export function getHandleStyle(isDark) {
    return {
        width: SHEET_TOKENS.sizes.dragHandle.width,
        height: SHEET_TOKENS.sizes.dragHandle.height,
        borderRadius: 99,
        background: isDark ? SHEET_TOKENS.handle.dark : SHEET_TOKENS.handle.light,
    };
}
